import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Send } from "lucide-react";
import axios from "../../../../axios";
import { toast } from "react-toastify";

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getPayload = (response) =>
  response?.data?.result || response?.data?.data || response?.data || {};

const getApiMessage = (source, fallback) =>
  source?.response?.data?.message ||
  source?.data?.message ||
  source?.message ||
  fallback;

const getQuestionsFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;
  const candidates = [
    result?.questions,
    result?.assignedQuestions,
    result?.questionList,
    result?.mockTest?.questions,
    data?.questions,
    data?.assignedQuestions,
    data?.questionList,
    Array.isArray(result) ? result : null,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const getQuestionId = (question, fallback) =>
  question?._id ||
  question?.question?._id ||
  question?.questionId?._id ||
  question?.questionId ||
  String(fallback);

const getQuestionSource = (question) => {
  if (question?.question && typeof question.question === "string") return question;

  return (
    question?.question ||
    question?.questionId ||
    question?.mockQuestion ||
    question?.mockTestQuestion ||
    question?.questionData ||
    question
  );
};

const isImageValue = (value) =>
  typeof value === "string" &&
  /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(value.trim());

const getOptionValue = (option) =>
  pickFirst(
    option?.text,
    option?.label,
    option?.value,
    option?.option,
    option?.answer,
    option?.title,
    option,
  );

const getOptionImage = (option) =>
  pickFirst(option?.image, option?.imageUrl, option?.optionImage, option?.url);

const getQuestionImage = (question) => {
  const source = getQuestionSource(question);

  return pickFirst(
    question?.questionImage,
    question?.questionImageUrl,
    question?.image,
    question?.imageUrl,
    question?.fileUrl,
    source?.questionImage,
    source?.questionImageUrl,
    source?.image,
    source?.imageUrl,
    source?.fileUrl,
  );
};

const getQuestionText = (question, index) => {
  const source = getQuestionSource(question);
  const questionText = pickFirst(
    question?.questionText,
    question?.title,
    question?.name,
    source?.questionText,
    source?.question,
    source?.questionEng,
    source?.questionHin,
    source?.englishQuestion,
    source?.title,
    source?.name,
  );

  return questionText && !isImageValue(questionText)
    ? questionText
    : `Question ${index + 1}`;
};

const getOptions = (question) => {
  const source = getQuestionSource(question);
  const optionList = pickFirst(
    source?.options,
    source?.optionsEng,
    source?.englishOptions,
    source?.answerOptions,
    question?.options,
    question?.optionsEng,
  );

  if (Array.isArray(optionList)) {
    return optionList
      .map((option) => ({
        value: String(getOptionValue(option) || ""),
        image: getOptionImage(option),
      }))
      .filter((option) => option.value || option.image);
  }

  const optionFields = [
    source?.optionA,
    source?.optionB,
    source?.optionC,
    source?.optionD,
    source?.option1,
    source?.option2,
    source?.option3,
    source?.option4,
    source?.a,
    source?.b,
    source?.c,
    source?.d,
  ];

  return optionFields
    .map((option) => ({
      value: String(getOptionValue(option) || ""),
      image: getOptionImage(option),
    }))
    .filter((option) => option.value || option.image);
};

const formatTime = (seconds) => {
  const value = Math.max(Number(seconds || 0), 0);
  const mins = Math.floor(value / 60);
  const secs = value % 60;

  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const MyTestFlow = () => {
  const { mockTestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const submitLockRef = useRef(false);
  const [mockTest, setMockTest] = useState(null);
  const [attemptId, setAttemptId] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [deadlineMs, setDeadlineMs] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const routePassKey = location.state?.passKey?.trim() || "";
  const startAlreadyCalled = Boolean(location.state?.startAlreadyCalled);
  const startData = location.state?.startData;

  useEffect(() => {
    const startMyTest = async () => {
      setLoading(true);

      try {
        const response = startAlreadyCalled
          ? { data: startData }
          : await axios.post(
              `/institute/mock-tests/${mockTestId}/start`,
              routePassKey ? { passKey: routePassKey } : {},
            );
        const startMessage = getApiMessage(response, "");
        const questionsResponse = await axios.get(
          `/institute/mock-tests/${mockTestId}/questions`,
        );
        const payload = getPayload(response);
        const questionList =
          getQuestionsFromResponse(questionsResponse).length > 0
            ? getQuestionsFromResponse(questionsResponse)
            : getQuestionsFromResponse(response);
        const test = payload?.mockTest || payload?.test || payload;
        const durationSeconds = Math.max(Number(test?.durationMinutes || 0), 0) * 60;
        const expiresAtMs = payload?.expiresAt
          ? new Date(payload.expiresAt).getTime()
          : Date.now() + durationSeconds * 1000;
        const activeAttemptId = pickFirst(
          payload?.attempt?._id,
          payload?.attemptId,
          payload?.attempt,
          payload?._id,
        );

        if (!questionList.length) {
          toast.error("No questions are available for this test");
          return;
        }

        setMockTest(test);
        setAttemptId(String(activeAttemptId || ""));
        setQuestions(questionList);
        setDeadlineMs(expiresAtMs);
        setTimeLeft(Math.max(Math.ceil((expiresAtMs - Date.now()) / 1000), 0));
        if (startMessage && !startAlreadyCalled) {
          toast.success(startMessage);
        }
      } catch (error) {
        toast.error(getApiMessage(error, "Unable to start my test"));
      } finally {
        setLoading(false);
      }
    };

    startMyTest();
  }, [mockTestId, routePassKey, startAlreadyCalled, startData]);

  useEffect(() => {
    if (!deadlineMs || submitting) return undefined;

    const timer = window.setInterval(() => {
      const next = Math.max(Math.ceil((deadlineMs - Date.now()) / 1000), 0);
      setTimeLeft(next);

      if (next === 0) {
        window.clearInterval(timer);
      }
    }, 1000);

    return () => window.clearInterval(timer);
  }, [deadlineMs, submitting]);

  const currentQuestion = questions[currentIndex];
  const questionId = currentQuestion ? getQuestionId(currentQuestion, currentIndex) : "";
  const answeredCount = Object.keys(answers).length;

  const title = useMemo(
    () => pickFirst(mockTest?.title, mockTest?.name, mockTest?.mockTestName, "My Test"),
    [mockTest],
  );

  const handleAnswer = async (answer) => {
    if (!questionId) return;

    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        question: questionId,
        answer,
      },
    }));

    try {
      await axios.post(`/institute/mock-tests/${mockTestId}/answer`, {
        ...(attemptId ? { attempt: attemptId, attemptId } : {}),
        question: questionId,
        questionId,
        answer,
        selectedAnswer: answer,
      });
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to save answer"));
    }
  };

  const handleSubmit = async ({ auto = false } = {}) => {
    if (submitLockRef.current) return;

    submitLockRef.current = true;
    setSubmitting(true);

    try {
      const response = await axios.post(`/institute/mock-tests/${mockTestId}/submit`, {
        ...(attemptId ? { attempt: attemptId, attemptId } : {}),
        answers: Object.values(answers),
      });

      toast.success(
        getApiMessage(
          response,
          auto ? "Time up! Test submitted automatically" : "Test submitted successfully",
        ),
      );
      navigate(`/dash/my-test-result/${mockTestId}`);
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to submit my test"));
      if (!auto) {
        submitLockRef.current = false;
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (timeLeft === 0 && questions.length && !loading && !submitting) {
      handleSubmit({ auto: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fbfafc]">
        <Loader2 className="h-9 w-9 animate-spin text-[#562566]" />
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-[#fbfafc] px-4 py-10 text-center">
        <div className="mx-auto max-w-xl rounded-2xl bg-white p-8 shadow-sm">
          <h1 className="text-xl font-extrabold text-[#211629]">No questions found</h1>
          <button
            type="button"
            onClick={() => navigate("/dash/my-mock-tests")}
            className="mt-5 rounded-full bg-[#562566] px-5 py-3 text-sm font-bold text-white"
          >
            Back to My Test
          </button>
        </div>
      </div>
    );
  }

  const options = getOptions(currentQuestion);
  const selectedAnswer = answers[questionId]?.answer || "";
  const isLastQuestion = currentIndex === questions.length - 1;
  const questionText = getQuestionText(currentQuestion, currentIndex);
  const questionImage = getQuestionImage(currentQuestion);
  const questionHin = currentQuestion?.questionHin || getQuestionSource(currentQuestion)?.questionHin;

  return (
    <div className="min-h-screen bg-[#fbfafc] pb-24 text-[#211629]">
      <header className="sticky top-0 z-20 border-b border-[#eee8f0] bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f7f2f8] text-[#562566]"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-extrabold">{title}</h1>
            <p className="text-xs font-semibold text-gray-500">
              Question {currentIndex + 1} of {questions.length}
            </p>
          </div>
          <span className="rounded-full bg-[#562566] px-4 py-2 text-sm font-extrabold text-white">
            {formatTime(timeLeft)}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-5">
        <section className="rounded-3xl bg-white p-5 text-left shadow-[0_8px_22px_rgba(33,22,41,0.10)]">
          <p className="text-sm font-extrabold text-[#562566]">
            Q{currentIndex + 1}
          </p>
          <h2 className="mt-2 text-lg font-semibold leading-7">
            {questionText}
          </h2>
          {questionHin && (
            <p className="mt-2 text-sm font-semibold leading-6 text-gray-600">
              {questionHin}
            </p>
          )}
          {questionImage && (
            <img
              src={questionImage}
              alt={`Question ${currentIndex + 1}`}
              className="mt-4 max-h-80 w-full rounded-2xl object-contain"
            />
          )}

          <div className="mt-5 space-y-3">
            {options.map((option, index) => {
              const optionText = option.value;
              const optionImage = option.image || (isImageValue(optionText) ? optionText : "");
              const active = selectedAnswer === optionText;

              return (
                <button
                  key={`${questionId}-${index}`}
                  type="button"
                  onClick={() => handleAnswer(optionText)}
                  className={`flex min-h-[56px] w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-semibold ${
                    active
                      ? "border-[#562566] bg-[#f6eef9] text-[#562566]"
                      : "border-[#e5dde9] bg-white text-[#211629]"
                  }`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-white text-xs font-extrabold">
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span className="min-w-0 flex-1">
                    {optionImage ? (
                      <img
                        src={optionImage}
                        alt={`Option ${String.fromCharCode(65 + index)}`}
                        className="max-h-32 rounded-xl object-contain"
                      />
                    ) : (
                      optionText
                    )}
                  </span>
                </button>
              );
            })}
            {!options.length && (
              <div className="rounded-2xl bg-[#fff6e8] px-4 py-3 text-sm font-semibold text-[#9A5A00]">
                Options is question ke liye available nahi hain.
              </div>
            )}
          </div>
        </section>

        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={currentIndex === 0}
            onClick={() => setCurrentIndex((index) => Math.max(index - 1, 0))}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#f7f2f8] px-5 text-sm font-extrabold text-[#562566] disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </button>

          {isLastQuestion ? (
            <button
              type="button"
              disabled={submitting}
              onClick={() => handleSubmit()}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#F5A623] px-6 text-sm font-extrabold text-white disabled:opacity-70"
            >
              <Send className="h-4 w-4" />
              {submitting ? "Submitting..." : "Submit"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() =>
                setCurrentIndex((index) => Math.min(index + 1, questions.length - 1))
              }
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#562566] px-5 text-sm font-extrabold text-white"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>

        <p className="mt-4 text-center text-xs font-semibold text-gray-500">
          {answeredCount} of {questions.length} answered
        </p>
      </main>
    </div>
  );
};

export default MyTestFlow;
